import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
  type ReactNode,
} from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { ProductModalDetails } from '../../models/product';
import { Box, Stack, Typography, IconButton, Chip, useTheme, Skeleton } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { PrimaryButton } from '../Button';
import { ProductContext } from '../../context/products-context';
import * as styled from './styles';

const ProductInfo = ({
  name,
  categories,
  price,
  align = 'left',
  closeButton,
  titleSize = '1.25rem',
}: {
  name?: string;
  categories: string[];
  price: number;
  align?: 'left' | 'center';
  closeButton?: ReactNode;
  titleSize?: string;
}) => {
  const hasCategories = categories.length > 0;
  const isCentered = align === 'center';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {name != null && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: closeButton ? 'space-between' : undefined,
            gap: 1,
          }}
        >
          <Typography
            component="h2"
            sx={{
              fontWeight: 700,
              fontSize: titleSize,
              lineHeight: 1.25,
              letterSpacing: '-0.01em',
              textAlign: align,
              flex: closeButton ? 1 : undefined,
              minWidth: 0,
            }}
          >
            {name}
          </Typography>
          {closeButton}
        </Box>
      )}
      {hasCategories && (
        <Stack
          direction="row"
          flexWrap="wrap"
          spacing={isCentered ? 1 : 0.5}
          useFlexGap
          justifyContent={isCentered ? 'center' : 'flex-start'}
        >
          {categories.map((cat, i) =>
            isCentered ? (
              <Box
                key={`${cat}-${i}`}
                sx={{
                  bgcolor: 'grey.100',
                  color: 'text.secondary',
                  px: 1.75,
                  py: 1,
                  borderRadius: 2,
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  lineHeight: 1.3,
                }}
              >
                {cat}
              </Box>
            ) : (
              <Chip
                key={`${cat}-${i}`}
                label={cat}
                size="small"
                variant="outlined"
                sx={{ borderColor: 'grey.300', color: 'text.secondary' }}
              />
            ),
          )}
        </Stack>
      )}
      <Typography
        component="p"
        sx={{ fontWeight: 700, textAlign: align, fontSize: '1.25rem' }}
      >
        R{price}
      </Typography>
    </Box>
  );
}

function DescriptionBlock({ description, align = 'left' }: { description?: string; align?: 'left' | 'center' }) {
  if (!description?.trim()) return null;
  return (
    <Typography
      variant="body1"
      color="text.secondary"
      sx={{ lineHeight: 1.65, textAlign: align, fontSize: '1rem' }}
    >
      {description}
    </Typography>
  );
}


const ImageCarousel = ({
  images,
  name,
  isMobile,
  show,
}: {
  images: string[];
  name: string;
  isMobile: boolean;
  show: boolean;
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const [heroReady, setHeroReady] = useState(false);

  const markHeroReady = useCallback(() => setHeroReady(true), []);

  useEffect(() => {
    if (show) setHeroReady(false);
  }, [show]);

  const firstImageRef = useCallback(
    (node: HTMLImageElement | null) => {
      if (node?.complete && node.naturalHeight > 0) markHeroReady();
    },
    [markHeroReady],
  );

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on('select', onSelect);
    onSelect();
    return () => { emblaApi.off('select', onSelect); };
  }, [emblaApi]);

  useEffect(() => {
    if (show && emblaApi) emblaApi.scrollTo(0, true);
  }, [show, emblaApi]);

  const showSideNav = !isMobile && images.length > 1;
  const showDots = images.length > 1;

  const navButtonSx = {
    flexShrink: 0,
    color: 'grey.700',
    border: 1,
    borderColor: 'grey.300',
    bgcolor: 'grey.50',
    '&:hover': { bgcolor: 'grey.200', borderColor: 'grey.400' },
  } as const;

  const carousel = (
    <Box
      sx={
        isMobile
          ? undefined
          : {
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
            }
      }
    >
      <Box
        ref={emblaRef}
        sx={{
          overflow: 'hidden',
          borderRadius: 1,
          ...(isMobile ? {} : { flex: 1, minHeight: 0 }),
        }}
      >
        <Box sx={{ display: 'flex', height: isMobile ? 'auto' : '100%' }}>
          {images.map((src, index) => (
            <Box
              key={src}
              sx={{
                flex: '0 0 100%',
                minWidth: 0,
                minHeight: isMobile ? undefined : 0,
                height: isMobile ? 'auto' : '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Box
                component="img"
                ref={index === 0 ? firstImageRef : undefined}
                src={src}
                alt={`${name} — product`}
                loading={index === 0 ? 'eager' : 'lazy'}
                decoding="async"
                {...(index === 0 ? { fetchPriority: 'high' } : {})}
                onLoad={index === 0 ? markHeroReady : undefined}
                onError={index === 0 ? markHeroReady : undefined}
                sx={{
                  maxWidth: '100%',
                  width: 'auto',
                  height: 'auto',
                  maxHeight: isMobile ? '45vh' : '100%',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            </Box>
          ))}
        </Box>
      </Box>
      {showDots && (
        <Stack
          direction="row"
          justifyContent="center"
          spacing={0.75}
          sx={{ mt: 1.5, flexShrink: 0 }}
        >
          {scrollSnaps.map((_, i) => (
            <styled.CarouselDot
              key={i}
              active={i === selectedIndex}
              onClick={() => emblaApi?.scrollTo(i)}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </Stack>
      )}
    </Box>
  );

  const wrappedCarousel = showSideNav ? (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        gap: 0.75,
        width: '100%',
        ...(isMobile ? {} : { flex: 1, minHeight: 0 }),
      }}
    >
      <IconButton
        onClick={() => emblaApi?.scrollPrev()}
        aria-label="Previous slide"
        size="small"
        sx={{ ...navButtonSx, alignSelf: 'center' }}
      >
        <ChevronLeftIcon fontSize="small" />
      </IconButton>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {carousel}
      </Box>
      <IconButton
        onClick={() => emblaApi?.scrollNext()}
        aria-label="Next slide"
        size="small"
        sx={{ ...navButtonSx, alignSelf: 'center' }}
      >
        <ChevronRightIcon fontSize="small" />
      </IconButton>
    </Box>
  ) : (
    carousel
  );

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        ...(isMobile
          ? { minHeight: 'min(40vh, 320px)' }
          : {
              flex: 1,
              minHeight: 0,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }),
      }}
    >
      {!heroReady && (
        <Skeleton
          variant="rounded"
          animation="wave"
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            height: '100%',
            minHeight: isMobile ? 'min(40vh, 320px)' : 0,
            borderRadius: 1,
          }}
          aria-hidden
        />
      )}
      <Box
        sx={{
          opacity: heroReady ? 1 : 0,
          transition: 'opacity 0.2s ease-out',
          pointerEvents: heroReady ? 'auto' : 'none',
          ...(isMobile
            ? {}
            : {
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
              }),
        }}
      >
        {wrappedCarousel}
      </Box>
    </Box>
  );
}

const ProductModal = (details: ProductModalDetails) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { addToCart } = useContext(ProductContext);
  const { name, images, show, setModalIsOpen, price, description, categories } = details;

  const safeCategories = Array.isArray(categories) ? categories : [];
  const safeImages = Array.isArray(images) ? images : [];

  useLayoutEffect(() => {
    if (!show || !isMobile) return undefined;
    const html = document.documentElement;
    const body = document.body;
    const prev = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyRight: body.style.right,
      bodyWidth: body.style.width,
    };
    const scrollY = window.scrollY;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    return () => {
      html.style.overflow = prev.htmlOverflow;
      body.style.overflow = prev.bodyOverflow;
      body.style.position = prev.bodyPosition;
      body.style.top = prev.bodyTop;
      body.style.left = prev.bodyLeft;
      body.style.right = prev.bodyRight;
      body.style.width = prev.bodyWidth;
      window.scrollTo(0, scrollY);
    };
  }, [show, isMobile]);

  const closeModal = () => setModalIsOpen(false);
  const handleAddToCart = () => { addToCart(name); closeModal(); };

  const closeIconButton = (
    <IconButton
      onClick={closeModal}
      aria-label="Close"
      size="small"
      sx={{
        bgcolor: 'grey.200',
        flexShrink: 0,
        width: 40,
        height: 40,
        '&:hover': { bgcolor: 'grey.300' },
      }}
    >
      <CloseIcon fontSize="small" />
    </IconButton>
  );

  const carouselBlock =
    safeImages.length > 0 ? (
      <ImageCarousel images={safeImages} name={name} isMobile={isMobile} show={show} />
    ) : (
      <Box
        sx={{
          minHeight: 200,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.100',
          borderRadius: 1,
        }}
        aria-hidden
      >
        <Typography variant="body2" color="text.secondary">
          No image
        </Typography>
      </Box>
    );

  return (
    <styled.ProductModalRoot
      open={show}
      onClose={closeModal}
      maxWidth="md"
      fullWidth
      sheetLayout={isMobile}
      disableScrollLock={isMobile}
      scroll="paper"
    >
      {isMobile && (
        <styled.ProductModalHeader>
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Typography
              component="h2"
              sx={{ fontWeight: 700, fontSize: '1.125rem', lineHeight: 1.3, flex: 1, textAlign: 'left' }}
            >
              {name}
            </Typography>
            {closeIconButton}
          </Box>
        </styled.ProductModalHeader>
      )}

      <styled.ProductModalBody sheetLayout={isMobile}>
        <Box sx={{ px: { xs: 2, md: 3 }, pb: { xs: 1, md: 2 }, pt: { xs: 0, md: 2 } }}>
          {isMobile ? (
            <Stack spacing={1}>
              <ProductInfo
                categories={safeCategories}
                price={price}
                align="center"
              />
              <Box sx={{ width: '100%' }}>{carouselBlock}</Box>
              <DescriptionBlock description={description} align="center" />
            </Stack>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'stretch',
                minHeight: 280,
                height: 'min(56vh, 540px)',
                maxHeight: 'min(76vh, 640px)',
              }}
            >
              <Box
                sx={{
                  flex: '1 1 50%',
                  minWidth: 0,
                  minHeight: 0,
                  height: '100%',
                  pr: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                }}
              >
                {carouselBlock}
              </Box>

              <Box
                aria-hidden
                sx={{ width: '1px', flexShrink: 0, alignSelf: 'stretch', bgcolor: 'grey.300' }}
              />

              <Box
                sx={{
                  flex: '1 1 50%',
                  minWidth: 0,
                  pl: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  height: '100%',
                }}
              >
                <ProductInfo
                  name={name}
                  categories={safeCategories}
                  price={price}
                  align="left"
                  closeButton={closeIconButton}
                  titleSize="1.5rem"
                />
                <Box
                  sx={{
                    flex: '1 1 auto',
                    minHeight: description?.trim() ? 80 : 0,
                    overflowY: 'auto',
                    my: description?.trim() ? 2 : 0,
                    pr: 0.5,
                  }}
                >
                  <DescriptionBlock description={description} />
                </Box>
                <Box sx={{ flexShrink: 0, pt: 1 }}>
                  <PrimaryButton onClick={handleAddToCart} width="100%" style={{ margin: 0 }}>
                    Add to cart
                  </PrimaryButton>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </styled.ProductModalBody>

      {isMobile && (
        <styled.ProductModalSheetFooter>
          <Box
            sx={{
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
              boxSizing: 'border-box',
              px: 2,
              py: 1.5,
            }}
          >
            <PrimaryButton
              onClick={handleAddToCart}
              width="100%"
              style={{ margin: 0, maxWidth: '100%', boxSizing: 'border-box' }}
            >
              Add to cart
            </PrimaryButton>
          </Box>
        </styled.ProductModalSheetFooter>
      )}
    </styled.ProductModalRoot>
  );
};

export default ProductModal;
